import { MAX_FILE_NAME_LENGTH, UPLOAD_FILE_MAX_BYTES } from 'shared/file/models/fileTypePolicy'
import { rewriteImageSources } from './newsInlineImages'

export const MAX_NEWS_ATTACHMENTS = 10

// Mirrors the server's denylist (`MimeTypes`, `Filenames.FORBIDDEN_FILE_EXTENSIONS`), so the admin
// hears about a forbidden file before saving rather than from a failed upload afterwards.
const FORBIDDEN_MIME_TYPES = ['image/svg+xml', 'text/html', 'application/xhtml+xml']
const FORBIDDEN_EXTENSIONS = [
  'svg', 'html', 'htm', 'xhtml',
  'exe', 'com', 'scr', 'pif', 'bat', 'cmd', 'msi', 'msp', 'cpl', 'dll', 'jar', 'app', 'deb', 'rpm', 'reg',
  'sh', 'bash', 'zsh', 'ps1', 'psm1', 'vbs', 'vbe', 'js', 'jse', 'wsf', 'wsh', 'hta', 'lnk',
]

export type NewsAttachmentRejection =
  | { reason: 'empty' }
  | { reason: 'tooLarge'; maxBytes: number }
  | { reason: 'nameBlank' }
  | { reason: 'nameTooLong'; maxLength: number }
  | { reason: 'forbiddenType' }
  | { reason: 'limitReached'; max: number }

// The server's `Filenames.canonicalize`: the display name the server stores, and the one its
// length limit is measured on.
export function canonicalizeNewsFileName(name: string): string {
  return name
    .replace(/[\p{Cc}\p{Cf}]/gu, '')
    .replace(/^[\s\p{Z}]+/u, '')
    .replace(/[\s\p{Z}.]+$/u, '')
}

const extensionOf = (name: string) => {
  const dot = name.lastIndexOf('.')
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase()
}

// `allowedNewCount` is how many new files still fit next to the kept attachments; files from that
// index on are over the limit.
export function rejectNewsAttachment(file: File, index: number, allowedNewCount: number): NewsAttachmentRejection | null {
  if (file.size === 0) {
    return { reason: 'empty' }
  }
  if (file.size > UPLOAD_FILE_MAX_BYTES) {
    return { reason: 'tooLarge', maxBytes: UPLOAD_FILE_MAX_BYTES }
  }

  const name = canonicalizeNewsFileName(file.name)
  if (name === '') {
    return { reason: 'nameBlank' }
  }
  if (name.length > MAX_FILE_NAME_LENGTH) {
    return { reason: 'nameTooLong', maxLength: MAX_FILE_NAME_LENGTH }
  }
  if (FORBIDDEN_MIME_TYPES.includes(file.type) || FORBIDDEN_EXTENSIONS.includes(extensionOf(name))) {
    return { reason: 'forbiddenType' }
  }
  if (index >= allowedNewCount) {
    return { reason: 'limitReached', max: MAX_NEWS_ATTACHMENTS }
  }

  return null
}

// Pasted HTML can carry images inlined as base64 `data:` sources, which would bloat the stored body.
// Only those are dropped; images linked by address stay.
export function stripInlineImages(html: string): string {
  return rewriteImageSources(html, (src) => (src.trim().toLowerCase().startsWith('data:') ? null : src))
}

export function containsImage(html: string): boolean {
  const template = document.createElement('template')
  template.innerHTML = html
  return template.content.querySelector('img') !== null
}
